import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Space,
  Input,
  Modal,
  Form,
  message,
  Tooltip,
  Select,
  Dropdown,
  Menu,
  Popconfirm
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  ExportOutlined,
  SettingOutlined,
  EditOutlined,
  LinkOutlined,
  DeleteOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { apiService } from '../../../services/apiService';

const { Search } = Input;

interface ShopifyAccount {
  id: string;
  storeName: string;
  baseUrl: string;
  status: 'connected' | 'disconnected';
  accessToken: string;
  apiVersion: string;
  lastConnected: string | null;
  customerId?: string | null;
  customerName?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface Customer {
  id: string;
  name: string;
  email?: string;
  isActive: boolean;
}

type TableRow = ShopifyAccount;

const ShopifyAdminAccounts: React.FC = () => {
  const [accounts, setAccounts] = useState<ShopifyAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editingAccount, setEditingAccount] = useState<ShopifyAccount | null>(null);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [filteredAccounts, setFilteredAccounts] = useState<ShopifyAccount[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);

  const [form] = Form.useForm();

  // 初始化數據
  useEffect(() => {
    fetchAccounts();
    fetchCustomers();
  }, []);

  // 過濾數據
  useEffect(() => {
    const filtered = accounts.filter(account => {
      const matchesSearch = account.storeName.toLowerCase().includes(searchText.toLowerCase());
      const matchesStatus = !statusFilter || account.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
    setFilteredAccounts(filtered);
  }, [accounts, searchText, statusFilter]);

  const tableData: TableRow[] = filteredAccounts;

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (searchText) {
        params.search = searchText;
      }
      if (statusFilter) {
        params.status = statusFilter;
      }
      const result: any = await apiService.get('/shopify/accounts', { params });
      if (result?.success) {
        setAccounts(result.data || []);
      } else {
        message.error(result?.message || '無法載入 Shopify 帳號列表');
        setAccounts([]);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      message.error('無法載入 Shopify 帳號列表');
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    setCustomersLoading(true);
    try {
      const result: any = await apiService.get('/customers', { params: { isActive: 'true', limit: 1000 } });
      if (result?.customers) {
        setCustomers(result.customers);
      } else if (Array.isArray(result)) {
        setCustomers(result);
      } else {
        setCustomers([]);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
    } finally {
      setCustomersLoading(false);
    }
  };

  const handleEdit = (record: ShopifyAccount) => {
    setEditingAccount(record);
    form.setFieldsValue({
      storeName: record.storeName,
      baseUrl: record.baseUrl,
      accessToken: record.accessToken,
      apiVersion: record.apiVersion,
      customerId: record.customerId || undefined,
    });
    setEditModalVisible(true);
  };

  const handleAdd = () => {
    setEditingAccount(null);
    form.resetFields();
    setAddModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Handle customerId: convert undefined to null for clearing the link
      const payload = {
        ...values,
        customerId: values.customerId || null,
      };

      if (editingAccount) {
        // 更新現有賬戶
        const result: any = await apiService.put(`/shopify/accounts/${editingAccount.id}`, payload);
        if (result?.success) {
          message.success('賬戶更新成功');
          setEditModalVisible(false);
          fetchAccounts();
        } else {
          message.error(result?.message || '賬戶更新失敗');
        }
      } else {
        // 添加新賬戶
        const result: any = await apiService.post('/shopify/accounts', payload);
        if (result?.success) {
          message.success('賬戶添加成功');
          setAddModalVisible(false);
          fetchAccounts();
        } else {
          message.error(result?.message || '賬戶添加失敗');
        }
      }
    } catch (error) {
      console.error('Error saving account:', error);
      message.error('保存失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (record: ShopifyAccount) => {
    try {
      setLoading(true);
      const result: any = await apiService.delete(`/shopify/accounts/${record.id}`);
      if (result?.success) {
        message.success('賬戶刪除成功');
        fetchAccounts();
      } else {
        message.error(result?.message || '賬戶刪除失敗');
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      message.error('刪除失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async (record: ShopifyAccount) => {
    try {
      message.loading({ content: '測試連接中...', key: 'test-connection' });
      const result: any = await apiService.post(`/shopify/accounts/${record.id}/test`);
      if (result?.success) {
        message.success({ content: '連接測試成功', key: 'test-connection' });
        fetchAccounts();
      } else {
        message.error({ content: result?.message || '連接測試失敗', key: 'test-connection' });
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      message.error({ content: '連接測試失敗', key: 'test-connection' });
    }
  };

  const handleExport = () => {
    try {
      // 導出功能實現
      const csvContent = [
        ['StoreName', 'BaseUrl', 'Status', 'AccessToken', 'Last Connected'].join(','),
        ...filteredAccounts.map(account => [
          account.storeName,
          account.baseUrl,
          account.status,
          account.accessToken,
          account.lastConnected
        ].join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'shopify-accounts.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      message.success('導出成功');
    } catch (error) {
      message.error('導出失敗');
    }
  };

  // 列配置下拉菜單
  const columnSettingsMenu = (
    <Menu>
      <Menu.Item key="1">顯示/隱藏列</Menu.Item>
      <Menu.Item key="2">重置列設置</Menu.Item>
    </Menu>
  );

  const columns: ColumnsType<TableRow> = [
    {
      title: 'StoreName',
      dataIndex: 'storeName',
      key: 'storeName',
      sorter: (a, b) => a.storeName.localeCompare(b.storeName),
      width: 120,
      render: (text: string) => <span style={{ fontWeight: 500 }}>{text}</span>
    },
    {
      title: 'Base URL',
      dataIndex: 'baseUrl',
      key: 'baseUrl',
      width: 150,
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <span style={{ color: '#1890ff' }}>{text}</span>
        </Tooltip>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      filters: [
        { text: '已連線', value: 'connected' },
        { text: '未連線', value: 'disconnected' }
      ],
      onFilter: (value, record) => record.status === value,
      render: (status: string) => (
        <Tag
          color={status === 'connected' ? 'green' : 'red'}
          icon={status === 'connected' ? <CheckCircleOutlined /> : undefined}
        >
          {status === 'connected' ? '已連線' : '未連線'}
        </Tag>
      )
    },
    {
      title: 'Linked Customer',
      dataIndex: 'customerName',
      key: 'customerName',
      width: 150,
      render: (customerName: string | null, record: ShopifyAccount) => {
        if (!record.customerId || !customerName) {
          return <Tag color="default">預設客戶</Tag>;
        }
        return (
          <Tooltip title={`Customer ID: ${record.customerId}`}>
            <Tag color="blue">{customerName}</Tag>
          </Tooltip>
        );
      }
    },
    {
      title: 'AccessToken',
      dataIndex: 'accessToken',
      key: 'accessToken',
      width: 200,
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <span style={{ color: '#1890ff' }}>{text}</span>
        </Tooltip>
      )
    },
    {
      title: 'Last Connected',
      dataIndex: 'lastConnected',
      key: 'lastConnected',
      width: 160,
      sorter: (a, b) => {
        if (!a.lastConnected) return 1;
        if (!b.lastConnected) return -1;
        return new Date(a.lastConnected).getTime() - new Date(b.lastConnected).getTime();
      },
      render: (text: string) => {
        if (!text) return <span style={{ color: '#999' }}>未連接</span>;
        const date = new Date(text);
        return (
          <Space>
            <span style={{ color: '#1890ff' }}>{date.toLocaleDateString()}</span>
            <span style={{ color: '#666' }}>{date.toLocaleTimeString()}</span>
          </Space>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="編輯">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="測試連線">
            <Button 
              type="text" 
              icon={<LinkOutlined />}
              onClick={() => handleTestConnection(record)}
            />
          </Tooltip>
          <Tooltip title="刪除">
            <Popconfirm
              title="確定要刪除這個賬戶嗎？"
              onConfirm={() => handleDelete(record)}
              okText="確定"
              cancelText="取消"
            >
              <Button 
                type="text" 
                icon={<DeleteOutlined />} 
                danger
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card title="Shopify 連線帳號 (Admin)">
        <div style={{marginBottom: 16, display: 'flex', justifyContent: 'space-between'}}>
          <Space>
            <Search
              placeholder="搜索 Store Name"
              allowClear
              style={{width: 250}}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={fetchAccounts}
              prefix={<SearchOutlined/>}
            />
            <Select
              placeholder="狀態篩選"
              allowClear
              style={{width: 120}}
              value={statusFilter || undefined}
              onChange={(value) => setStatusFilter(value || '')}
            >
              <Select.Option value="connected">已連線</Select.Option>
              <Select.Option value="disconnected">未連線</Select.Option>
            </Select>
          </Space>

          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined/>}
              onClick={handleAdd}
            >
              新增帳號
            </Button>
            <Button
              icon={<ReloadOutlined/>}
              onClick={fetchAccounts}
              loading={loading}
            >
              刷新
            </Button>
            <Button
              icon={<ExportOutlined/>}
              onClick={handleExport}
            >
              導出
            </Button>
            <Dropdown overlay={columnSettingsMenu} trigger={['click']}>
              <Button icon={<SettingOutlined/>}>
                列設置
              </Button>
            </Dropdown>
          </Space>
        </div>
        <Table
          rowKey="id"
          dataSource={tableData}
          columns={columns}
          loading={loading}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 條記錄`,
          }}
          scroll={{x: 800}}
        />
      </Card>

      {/* 添加/編輯模態框 */}
      <Modal
        title={editingAccount ? '編輯賬戶' : '新增賬戶'}
        open={addModalVisible || editModalVisible}
        onOk={handleSave}
        onCancel={() => {
          setAddModalVisible(false);
          setEditModalVisible(false);
          form.resetFields();
        }}
        confirmLoading={loading}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            status: 'connected'
          }}
        >
          <Form.Item
            name="storeName"
            label="商店名稱"
            rules={[{ required: true, message: '請輸入商店名稱' }]}
          >
            <Input placeholder="請輸入商店名稱" />
          </Form.Item>

          <Form.Item
            name="baseUrl"
            label="Base URL"
            rules={[{ required: true, message: '請輸入Base URL' }]}
          >
            <Input placeholder="例如: regentify.myshopify.com" />
          </Form.Item>

          <Form.Item
            name="accessToken"
            label="Access Token"
            rules={[{ required: true, message: '請輸入Access Token' }]}
          >
            <Input.TextArea 
              placeholder="請輸入Access Token"
              rows={3}
            />
          </Form.Item>

          <Form.Item
            name="apiVersion"
            label="API Version"
            rules={[{ required: true, message: '請輸入API Version' }]}
          >
            <Input placeholder="例如: 2024-01" />
          </Form.Item>

          <Form.Item
            name="customerId"
            label="關聯客戶 (Linked Customer)"
            tooltip="選擇此商店訂單所屬的客戶。如不選擇，將使用預設的 Shopify 客戶。"
          >
            <Select
              placeholder="選擇客戶 (可選)"
              allowClear
              showSearch
              loading={customersLoading}
              filterOption={(input, option) =>
                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {customers.map(customer => (
                <Select.Option key={customer.id} value={customer.id}>
                  {customer.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ShopifyAdminAccounts;
